import { computed, makeObservable } from 'mobx';

import { PermissionApiClient } from 'api/permission-api-client';
import { AssureBaseStore, IAssureStoreConstructorOptions } from './assure-base.store';
import { IPermission } from 'dto/access-management/user-role-dto';
import { getErrorMessage } from 'common-utils';

export class PermissionStore extends AssureBaseStore<PermissionApiClient, IPermission> {
    
    public constructor(options: IAssureStoreConstructorOptions) {
        super(options);
        makeObservable(this);
    }

    protected get apiClient(): PermissionApiClient {
        return this.apiClientStore.apiClients.permission;
    }

    public async loadPermissions(): Promise<void> {
        this.setDataLoading(true);
        this.setError(undefined);
        try {
            this.setEntities(await this.apiClient.getPermissions());
        } catch (err) {
            this.setError(getErrorMessage(err));
        } finally {
            this.setDataLoading(false);
        }
    }

    @computed
    public get getPermissions(): IPermission[] {
        if (!this.hasEntities()) return [];
        return this.entities;
    }

    @computed
    public get permissionCodes(): string[] {
        if (!this.hasEntities()) return [];
        return this.entities.map((permission) => permission.code);
    }

    public getManagedPermissions(): Promise<IPermission[]> {
        return this.apiClient.getManagedPermissions();
    }

    public hasPermission(permissionCode: string): boolean {
        return this.permissionCodes.includes(permissionCode);
    }

    public async doWithPermission(permissionCode: string, action: () => Promise<unknown>, options?: { onError?: () => unknown }): Promise<unknown> {
        if (this.hasPermission(permissionCode)) {
            return action();
        }
        return options?.onError ? options.onError() : Promise.reject(new Error(`You don't have permission to ${permissionCode}`));
    }
}
